import _ from "lodash"
import moment from 'moment'
import Client from "data/_client"
import notificationService from "data/notification"
import { CalcPayload } from "data/interface/calc"
import { logEvent } from "data/lib/log-event.ts"
import { local } from "data/lib/local-json.ts"
import propertyService from "./property"


import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import { ApiResult } from "data/interface/api"

export type RentRollCalcData = {
  rentrollid: number
  tenant: string
  suite: string
  leaseInterval: { begin: string, end: string },
  output: {
    occupiedArea: number[],
    vacantArea: number[],
    baseRent: number[],
    vacantRent: number[],
    freeRent: number[],
    recoveries: number[],
    freeRecoveries: number[],
    miscRevenue: number[],
    freeMiscRevenue: number[],
    percentageRent: number[],
    freePercentageRent: number[],
    tenantImprovements: number[],
    leasingCommissions: number[],
    otherCapital: number[],
    poolMissingOriginal?: boolean,
    poolMissingGenerated?: boolean
  }

}

export type CalcData = {
  propertyid: number,
  analysisPeriodInterval: { begin: string, end: string },
  analysisPeriodLength: number,
  tenants: RentRollCalcData[]
}

type CalcResultValue = CalcData | null | { propertyid: number }
const id: string = "calc"
const initialState: ApiResult<CalcResultValue> = { id, state: "init", value: null }
const calcStream = new BehaviorSubject<ApiResult<CalcResultValue>>(initialState)


// private module logic //

function start(type: "folder" | "property", properties: number[]): Promise<object | { message: string }> {
  if (!properties.length) {
    const message = "You must select a property to begin a calculation"
    notificationService.notify({ type: "danger", message, dismiss: 2000 })
    return new Promise((_, reject) => reject({ message }))
  }

  properties.forEach((propertyid) => propertyService.setCalcPending(propertyid))

  const folderid = _.last(local('folderPath'))
  const profile: { email: string, user_id: string } = _.invoke(JSON, "parse", localStorage.getItem("auth.profile"))
  const payload: CalcPayload = {
    calcType: "full",
    email: profile.email,
    folderOrPropertyCalc: type,
    propertyIds: properties,
    userId: profile.user_id,
    folderId: folderid
  }
  return logEvent(`calc-${type}`, { source: { properties, folderid } })
    .then(() => Client.post(`msg/svc_calc`, payload))
    .then(res => {
      return res
    })
    .catch((err: { message: string }) => {
      notificationService.notify({ type: "danger", message: "Uh-oh! It looks like an error has occurred.", dismiss: 2000 })
      return err
    })
}

function cancel(propertyId: number): Promise<object | { message: string }> {
  return Client.post(`cancel/calc/${propertyId}`, {})
    .then(res => {
      return res
    })
    .catch((err: { message: string }) => {
      notificationService.notify({ type: "danger", message: "Uh-oh! It looks like an error has occurred.", dismiss: 2000 })
      return err
    })
}
function nextRealValue(propertyid: number): Promise<CalcData | { message: string }> {
  return calcStream.toPromise().then((result) => {
    const { state, value } = result
    if (state === 'busy')
      return nextRealValue(propertyid)
    else if (state === 'error')
      return result.error as { 'message': string }
    else if (!value || !('analysisPeriodInterval' in value))
      return { message: `no calc result available` }
    else if (value.propertyid !== propertyid)// state == ok or init
      return ({ message: `result has invalid propertyid` })
    else
      return value
  })
}
function fetchCalc(propertyid: number, force: boolean = false): Promise<CalcData | { message: string }> {
  const current = calcStream.getValue()
  if (!force && current.state === 'busy' && current.value && current.value.propertyid === propertyid)
    return nextRealValue(propertyid)
  if (current.value && current.value.propertyid === propertyid)
    calcStream.next({ id, state: 'busy', value: current.value })
  else
    calcStream.next({ id, state: 'busy', value: { propertyid } })
  return Client.get(`calc/property/${propertyid}/full`, { host: process.env.BASE_API }).then(calcData => {
    calcStream.next({ id, state: 'ok', value: calcData })
    return calcData
  }).catch((err: { message: string }) => {
    calcStream.next({ id, state: 'error', value: null, error: { message: 'error retreiving calc data' } })
    // no alarm for errors yet
    return err;
  })
}

function newCalc(): Promise<string> {
  const current = propertyService.get()
  if (current) {
    notificationService.notify({
      type: "info",
      message: "New calc started",
      dismiss: 3000
    })
  } else {
    notificationService.notify({
      type: "warn",
      message: "You must select a property to run new calc",
      dismiss: 3000
    })

    return Promise.reject("No property selected")
  }
  console.log(`calling fetchCalc from client request`)

  return fetchCalc(current.propertyid, true)
    .then(res => {
      const fileURL = "data:application/json;charset=utf-8," + encodeURIComponent(JSON.stringify(res))
      const fileLink = document.createElement('a')
      fileLink.href = fileURL
      fileLink.download = `calc_result.${moment(new Date).format("YYYY.MM.DD.hh.mm")}.json`
      fileLink.click()
      notificationService.notify({ type: "info", message: "New calc completed", dismiss: 2000 })
      return "success"
    })
    .catch(e => {
      const err = "New calc failed"
      notificationService.notify({ type: "danger", message: err, dismiss: 2000 })
      return err
    })
}
// public module interface //

const calcCheck = (property: any) => ['Calculated', 'Calculated with Warnings'].includes(_.get(property, 'calcdetail.status'))

const calcService = {
  calcCheck,
  stream: () => calcStream.asObservable(), // new calc
  value: () => calcStream.getValue().value, // new calc
  fetchCalc, //new calc
  start, // old calc
  cancel, // old calc
  newCalc
}

export default calcService
